export interface MatchColorSettings {
  luminance: number;
  colorIntensity: number;
  fade: number;
  neutralize: boolean;
}

interface ColorStatistics {
  mean: [number, number, number];
  deviation: [number, number, number];
}

export function colorStatistics(pixels: Uint8ClampedArray): ColorStatistics {
  if (pixels.length % 4 !== 0) throw new Error('Color statistics require RGBA pixels.');
  const sums = [0, 0, 0];
  const squares = [0, 0, 0];
  let count = 0;
  for (let offset = 0; offset < pixels.length; offset += 4) {
    if ((pixels[offset + 3] ?? 0) === 0) continue;
    count += 1;
    for (let channel = 0; channel < 3; channel += 1) {
      const value = pixels[offset + channel] ?? 0;
      sums[channel] = (sums[channel] ?? 0) + value;
      squares[channel] = (squares[channel] ?? 0) + value * value;
    }
  }
  if (!count) return { mean: [0, 0, 0], deviation: [1, 1, 1] };
  const mean = sums.map((sum) => sum / count) as [number, number, number];
  const deviation = squares.map((sum, channel) => Math.sqrt(Math.max(1, sum / count - (mean[channel] ?? 0) ** 2))) as [number, number, number];
  return { mean, deviation };
}

export function matchColorPixels(source: Uint8ClampedArray, target: Uint8ClampedArray, settings: MatchColorSettings): Uint8ClampedArray {
  if (source.length % 4 !== 0 || target.length % 4 !== 0) throw new Error('Match Color requires RGBA pixels.');
  const sourceStats = colorStatistics(source);
  const targetStats = colorStatistics(target);
  const luminance = Math.max(0, Math.min(2, settings.luminance));
  const intensity = Math.max(0, Math.min(2, settings.colorIntensity));
  const amount = 1 - Math.max(0, Math.min(1, settings.fade));
  const sourceNeutral = (sourceStats.mean[0] + sourceStats.mean[1] + sourceStats.mean[2]) / 3;
  const output = new Uint8ClampedArray(target);
  for (let offset = 0; offset < target.length; offset += 4) {
    if ((target[offset + 3] ?? 0) === 0) continue;
    const mapped = [0, 1, 2].map((channel) => {
      const sourceMean = sourceStats.mean[channel] ?? 0;
      const neutralizedMean = settings.neutralize ? sourceMean + (sourceNeutral - sourceMean) * 0.75 : sourceMean;
      const centered = ((target[offset + channel] ?? 0) - (targetStats.mean[channel] ?? 0))
        * ((sourceStats.deviation[channel] ?? 1) / Math.max(1, targetStats.deviation[channel] ?? 1))
        * intensity;
      return neutralizedMean + centered;
    });
    const mappedLuminance = mapped[0]! * 0.2126 + mapped[1]! * 0.7152 + mapped[2]! * 0.0722;
    const targetLuminance = (target[offset] ?? 0) * 0.2126 + (target[offset + 1] ?? 0) * 0.7152 + (target[offset + 2] ?? 0) * 0.0722;
    const desiredLuminance = targetLuminance + (mappedLuminance - targetLuminance) * luminance;
    const luminanceShift = desiredLuminance - mappedLuminance;
    for (let channel = 0; channel < 3; channel += 1) {
      const original = target[offset + channel] ?? 0;
      output[offset + channel] = Math.round(Math.max(0, Math.min(255, original + ((mapped[channel] ?? original) + luminanceShift - original) * amount)));
    }
  }
  return output;
}
